import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";
import logoImage from "../../../assets/images/logo.png";
import invokeApi from "../../../services/invokeApi";

// API 1: Datos iniciales (se llama al cargar la página)
const getDatosIniciales = async (id_venta) => {
  try {
    const url = `/agenda/contenidoAccederGenerar?id_venta=${id_venta}`;
    const response = await invokeApi(url, "get");
    
    // Verificar si hay error en la respuesta
    if (response.error) {
      throw new Error(response.error.message || "Error al obtener datos iniciales");
    }
    
    // Verificar estructura de respuesta exitosa
    if (response.statusCode === 200 && response.comprador && response.contenido && response.profesional) {
      return response;
    } else {
      throw new Error("Respuesta con formato inesperado");
    }
  } catch (error) {
    console.error("Error en getDatosIniciales:", error);
    throw error;
  }
};

// Componente para mostrar estado de error
const ErrorState = () => {
  return (
    <>
      {/* Logo arriba */}
      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />
      
      {/* Caja morada clara central con mensaje */}
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { md: "18px", xs: "16px" },
            color: "#9e9e9e",
            textAlign: "center",
          }}
        >
          Contenido no disponible
        </Typography>
      </Box>
    </>
  );
};




const renderImageWithBlur = (src, alt, width, height, imageId, contentImageLoadingStates, setContentImageLoadingStates) => {
  const isImageLoadingState = contentImageLoadingStates[imageId] !== false;

  const containerStyle = {
    position: "relative",
    width: width || (isMobile ? "100%" : "100%"),
    height: height || (isMobile ? "250px" : "400px"),
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  const blurBackgroundStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `url(${src})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    filter: "blur(10px)",
    transform: "scale(1.1)",
    zIndex: 1
  };

  const handleImageLoad = () => {
    setContentImageLoadingStates(prev => ({
      ...prev,
      [imageId]: false
    }));
  };

  return (
    <Box sx={containerStyle}>
      {/* Imagen de fondo con blur */}
      {src && !isImageLoadingState && (
        <Box sx={blurBackgroundStyle} />
      )}
      
      {/* Imagen principal */}
      {src && (
        <img
          src={src}
          alt={alt}
          style={{
            position: "relative",
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            zIndex: 2,
            display: isImageLoadingState ? "none" : "block"
          }}
          onLoad={handleImageLoad}
        />
      )}
      
      {/* Skeleton usando el mismo patrón que la imagen principal */}
      {(isImageLoadingState || !src) && (
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height="100%"
          animation="wave"
          sx={{ 
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 3
          }} 
        />
      )}
    </Box>
  );
};

const ProfessionalInfo = ({ profesionalInfo, contenidoNombre, isLoading }) => {
  if (isLoading) {
    // El Skeleton se mantiene igual pero simplificado, ya que no necesita la versión de escritorio.
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton animation="wave" variant="text" width="80%" height={40} sx={{ mb: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="text" width={150} height={25} />
          <Skeleton variant="text" width={200} height={30} />
          <Skeleton variant="text" width={180} height={25} />
        </Box>
      </Box>
    );
  }

  if (!profesionalInfo) {
    return <Typography>No se encontró información del profesional.</Typography>;
  }

  const { profesional_nombres, profesional_apellidos, profesional_profesion, profesional_link_plataforma } = profesionalInfo;
  const nombreCompleto = `${profesional_nombres} ${profesional_apellidos}`;

  // Función para manejar el click en el nombre del profesional
  const handleProfessionalClick = () => {
    if (profesional_link_plataforma) {
      const profileUrl = `https://clinifica.com/pro/${profesional_link_plataforma}`;
      window.open(profileUrl, '_blank');
    }
  };

  // AHORA ESTE ES EL ÚNICO RETORNO POSIBLE (aparte de los de carga y error)
  return (
    <Box sx={{ mb: 3 }}> {/* Ajustamos el margen inferior (mb) a 3 para consistencia */}
      <Typography 
        sx={{ 
          fontFamily: "Quicksand-Regular", 
          color: "#191C1F",
          mb: 0,
          fontSize: { md: "16px", xs: "14px" } 
        }}
      >Creado por:</Typography>        
      <Typography 
        onClick={handleProfessionalClick}
        sx={{ 
          fontFamily: "Quicksand-Bold", 
          color: "#191C1F",
          mb: 0,
          fontSize: { md: "18px", xs: "16px" },
          cursor: profesional_link_plataforma ? "pointer" : "default",
          "&:hover": profesional_link_plataforma ? {
            color: "#3B82F6"
          } : {}
        }}
      >
        {nombreCompleto}
      </Typography>
      <Typography 
        sx={{ 
          fontFamily: "Quicksand-Regular", 
          fontSize: { md: "16px", xs: "14px" },
          mb: 0,
          color: "#191C1F"
        }}
      >
        {profesional_profesion}
      </Typography>
    </Box>
  );
};

const getFieldContainerStyle = () => ({
  borderRadius: 2,
  overflow: "hidden",
  backgroundImage: "linear-gradient(to bottom, #e7eff5, #f2ebf6)",
  backgroundColor: "transparent",
  p: 0.5,
});

const truncarNombre = (nombre, maxLength = isMobile ? 25 : 35) => {
  if (nombre.length <= maxLength) return nombre;
  return nombre.substring(0, maxLength - 3) + '...';
};

// INICIO BLOQUE VIDEO YOUTUBE (copia de EventoDetalle)
// Función helper para extraer video ID de YouTube
const extraerYouTubeVideoId = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // Agregar https si no tiene protocolo
  let urlProcesada = url.trim();
  if (!urlProcesada.match(/^https?:\/\//)) {
    urlProcesada = `https://${urlProcesada}`;
  }

  try {
    const urlObj = new URL(urlProcesada);
    const hostname = urlObj.hostname.replace('www.', '');

    // Formato youtube.com/watch?v=VIDEO_ID
    if (hostname === 'youtube.com' && urlObj.pathname === '/watch') {
      const videoId = urlObj.searchParams.get('v');
      if (videoId && videoId.match(/^[a-zA-Z0-9_-]{11}$/)) return videoId;
    }

    // Formato youtu.be/VIDEO_ID
    if (hostname === 'youtu.be') {
      const videoId = urlObj.pathname.slice(1);
      if (videoId && videoId.match(/^[a-zA-Z0-9_-]{11}$/)) return videoId;
    }
  } catch (e) {
    return null;
  }

  return null;
};

// Componente para embed de YouTube
const YouTubeEmbed = ({ videoUrl }) => {
  const videoId = extraerYouTubeVideoId(videoUrl);
  
  // Si no hay video ID válido, no renderizar nada
  if (!videoId) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingBottom: "56.25%", // 16:9
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title="Video del contenido"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
        />
      </Box>
    </Box>
  );
};
// FIN BLOQUE VIDEO YOUTUBE

function ContenidoAcceder() {
  const { id_venta } = useParams();
  const [currentPhase, setCurrentPhase] = useState(1); // 1 = solicitar código, 2 = mostrar contenido
  const [codigo, setCodigo] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [contentImageLoadingStates, setContentImageLoadingStates] = useState({});
  const [hasInitialError, setHasInitialError] = useState(false);
  const [sesionDescargaExpirada, setSesionDescargaExpirada] = useState(false); // true cuando expira la descarga pero hay video en pantalla
  
  // Estados para transiciones visuales
  const [showCheck, setShowCheck] = useState(false);
  const [buttonFading, setButtonFading] = useState(false);
  const [contentTransitioning, setContentTransitioning] = useState(false);
  const [loadingFromCookies, setLoadingFromCookies] = useState(false);
  
  // Estados para simulación automática
  const [autoSimulation, setAutoSimulation] = useState(false);
  const [simulatedCode, setSimulatedCode] = useState("");
  
  // Estados para reenvío de código
  const [estadoReenvio, setEstadoReenvio] = useState('normal'); // normal, reenviando, reenviado
  
  // Estados para datos de las APIs
  const [datosIniciales, setDatosIniciales] = useState(null);
  const [contenidoCompleto, setContenidoCompleto] = useState(null);

  // Ref para controlar si ya se ejecutó la llamada inicial
  const hasLoadedInitialData = useRef(false);

// Función para iniciar simulación automática
const startAutoSimulation = (accessData) => {
  setAutoSimulation(true);
  setDatosIniciales(accessData.datosIniciales);
  
  // Simular escritura de código con círculos
  let currentLength = 0;
  const targetLength = 6; // 6 círculos
  
  const writeCircles = () => {
    if (currentLength < targetLength) {
      currentLength++;
      setSimulatedCode("●".repeat(currentLength));
      setTimeout(writeCircles, 150);
    } else {
      // Código completo, activar validación automática
      setTimeout(() => {
        setIsValidating(true);
        
        // Simular validación
        setTimeout(() => {
          setShowCheck(true);
          
          setTimeout(() => {
            setButtonFading(true);
          }, 1000);
          
          setTimeout(() => {
            setContentTransitioning(true);
          }, 1500);
          
          setTimeout(() => {
            // Guardar acceso temporal en localStorage incluyendo código usado
            try {
              const accesoTemporal = {
                timestamp: Date.now(),
                contenido: accessData.contenido,
                datosIniciales: accessData.datosIniciales,
                codigo_usado: accessData.codigo_usado,
                timestamp_validacion: Date.now(),
                id_venta: id_venta
              };
              localStorage.setItem(`temp_access_${id_venta}`, JSON.stringify(accesoTemporal));
            } catch (error) {
              console.error("Error al guardar acceso temporal:", error);
            }
            
            setContenidoCompleto(accessData.contenido);
            setCurrentPhase(2);
            setContentTransitioning(false);
            setAutoSimulation(false);
          }, 2300);
        }, 1000);
      }, 500);
    }
  };
  
  setTimeout(writeCircles, 300);
};

  // Función para verificar acceso temporal en localStorage
  const checkTempAccess = () => {
    // RESETEAR ESTADOS DE TRANSICIÓN VISUAL SIEMPRE AL INICIO
    setShowCheck(false);
    setButtonFading(false);
    setContentTransitioning(false);
    
    try {
      const tempAccess = localStorage.getItem(`temp_access_${id_venta}`);
      
      if (tempAccess) {
        const accessData = JSON.parse(tempAccess);
        const now = Date.now();
        const fifteenMinutes = 15 * 60 * 1000; // 15 minutos en ms
        
        if (now - accessData.timestamp < fifteenMinutes) {
          // Acceso válido, iniciar simulación en lugar de carga directa
          startAutoSimulation(accessData);
          return true;
        } else {
          // Acceso expirado, limpiar
          localStorage.removeItem(`temp_access_${id_venta}`);
        }
      }
    } catch (error) {
      console.error("Error al verificar acceso temporal:", error);
      localStorage.removeItem(`temp_access_${id_venta}`);
    }
    return false;
  };

  // Cargar datos iniciales al montar el componente
  useEffect(() => {
    const loadDatosIniciales = async () => {
      // Verificar primero si hay acceso temporal válido
      if (checkTempAccess()) {
        setIsLoading(false);
        return;
      }
      
      // RESETEAR ESTADOS DE TRANSICIÓN CUANDO NO HAY ACCESO TEMPORAL VÁLIDO
      setShowCheck(false);
      setButtonFading(false);
      setContentTransitioning(false);
      
      // Prevenir llamadas duplicadas
      if (hasLoadedInitialData.current || !id_venta) {
        return;
      }
      
      hasLoadedInitialData.current = true;
      setIsLoading(true);
      setError("");
      setHasInitialError(false);
      
      try {
        const response = await getDatosIniciales(id_venta);
        setDatosIniciales(response);
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error);
        setHasInitialError(true);
        hasLoadedInitialData.current = false; // Permitir retry en caso de error
      } finally {
        setIsLoading(false);
      }
    };
  
    loadDatosIniciales();
  }, [id_venta]);

  // Scroll to top when component mounts
useEffect(() => {
  setTimeout(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, 0);
}, []);

// Verificación de expiración cada minuto
useEffect(() => {
  const verificarExpiracion = () => {
    if (currentPhase === 2) {
      try {
        const tempAccess = localStorage.getItem(`temp_access_${id_venta}`);
        if (tempAccess) {
          const accessData = JSON.parse(tempAccess);
          const now = Date.now();
          const fifteenMinutes = 15 * 60 * 1000;
          
          if (now - accessData.timestamp_validacion > fifteenMinutes) {
            limpiarAccesoYRedirigir();
          }
        } else {
          limpiarAccesoYRedirigir();
        }
      } catch (error) {
        console.error("Error al verificar expiración:", error);
        limpiarAccesoYRedirigir();
      }
    }
  };

  const interval = setInterval(verificarExpiracion, 60000);
  return () => clearInterval(interval);
}, [currentPhase, id_venta, contenidoCompleto]);

  const pageTitle = "Clinifica - Acceder a Contenido";
  const pageDescription = "Accede a tu contenido comprado.";

  const handleCodigoChange = (event) => {
    setCodigo(event.target.value);
    setError("");
    setWarningMessage("");
  };

  const handleValidarCodigo = async () => {
    if (!codigo.trim()) {
      setError("Debes ingresar el código");
      return;
    };
  
    setIsValidating(true);
    setError("");
  
    try {
      const url = `/agenda/contenidoAccederValidar?id_venta=${id_venta}&codigo=${codigo}`;
      const response = await invokeApi(url, "get");
      
      // Verificar si hay error en la respuesta
      // Verificar si hay error en la respuesta
      if (response.error) {
        const errorCode = response.error.serverStatusCode || response.error.code;
        const backendMessage = response.error.serverData?.error;
        
        if (errorCode === 400) {
          // Errores 400 van al box amarillo con mensaje específico del backend
          let warningText = "Código incorrecto"; // Default
          
          if (backendMessage) {
            if (backendMessage.includes("ya utilizado")) {
              warningText = "Código ya utilizado";
            } else if (backendMessage.includes("expirado")) {
              warningText = "Código expirado";
            } else if (backendMessage.includes("incorrecto")) {
              warningText = "Código incorrecto";
            }
          }
          
          setWarningMessage(warningText);
          setError(""); // Limpiar error rojo
        } else if (errorCode === 500) {
          // Errores 500 van al setError rojo
          setError("Error del servidor, intenta nuevamente");
          setWarningMessage(""); // Limpiar warning amarillo
        } else {
          // Otros errores van al setError rojo con mensaje genérico
          setError("Error desconocido, intenta nuevamente");
          setWarningMessage(""); // Limpiar warning amarillo
        }
        
        return;
      }
      
      // Verificar respuesta exitosa
      if (response.statusCode === 200) {
        // Guardar acceso temporal en localStorage incluyendo datos iniciales
        try {
          const tempAccess = {
            timestamp: Date.now(),
            contenido: response,
            datosIniciales: datosIniciales,
            codigo_usado: codigo,
            timestamp_validacion: Date.now(),
            id_venta: id_venta
          };
          localStorage.setItem(`temp_access_${id_venta}`, JSON.stringify(tempAccess));
        } catch (error) {
          console.error("Error al guardar acceso temporal:", error);
        }
        
        // Secuencia de transiciones visuales
        setShowCheck(true);
        
        setTimeout(() => {
          setButtonFading(true);
        }, 1000);
        
        setTimeout(() => {
          setContentTransitioning(true);
        }, 1500);
        
        setTimeout(() => {
          setContenidoCompleto(response);
          setCurrentPhase(2);
          setContentTransitioning(false);
        }, 2300);
      } else {
        setError("Error inesperado al validar el código");
      }
    } catch (error) {
      console.error("Error al validar código:", error);
      setError("Error al validar el código. Intenta nuevamente.");
    } finally {
      setIsValidating(false);
    }
  };

  const handleReenviarCodigo = async () => {
    setEstadoReenvio('reenviando');
    
    try {
      const url = `/agenda/contenidoAccederGenerarReenviar?id_venta=${id_venta}`;
      const response = await invokeApi(url, "get");
      
      // Verificar si hay error en la respuesta o código distinto a 200
      if (response.error || response.statusCode !== 200) {
        // Error: volver directamente a "reenviar"
        setEstadoReenvio('normal');
        return;
      }
      
      // Éxito: mostrar "reenviado" por 5 segundos
      setEstadoReenvio('reenviado');
      
      setTimeout(() => {
        setEstadoReenvio('normal');
      }, 5000);
      
    } catch (error) {
      console.error("Error al reenviar código:", error);
      // Error: volver directamente a "reenviar"
      setEstadoReenvio('normal');
    }
  };

  const limpiarAccesoYRedirigir = () => {
    try {
      // Limpiar localStorage
      localStorage.removeItem(`temp_access_${id_venta}`);
    } catch (error) {
      console.error("Error al limpiar localStorage:", error);
    }
    
    // Si hay un video en pantalla no se recarga: solo se bloquean las descargas
    if (extraerYouTubeVideoId(contenidoCompleto?.contenido?.contenido_video_url)) {
      setSesionDescargaExpirada(true);
      return;
    }
    
    // Siempre hacer refresh para garantizar estado limpio y nueva API call
    window.location.reload();
  };

  const handleDescargarArchivo = async (idArchivo) => {
    try {
      // Verificar que no haya expirado el acceso antes de descargar
      const tempAccess = localStorage.getItem(`temp_access_${id_venta}`);
      if (!tempAccess) {
        limpiarAccesoYRedirigir();
        return;
      }
      
      const accessData = JSON.parse(tempAccess);
      const now = Date.now();
      const fifteenMinutes = 15 * 60 * 1000;
      
      if (now - accessData.timestamp_validacion > fifteenMinutes) {
        limpiarAccesoYRedirigir();
        return;
      }
      
      // Extraer datos necesarios para la descarga
      const codigo_usado = accessData.codigo_usado;
      const id_contenido = contenidoCompleto?.contenido?.id_contenido;
      
      if (!codigo_usado || !id_contenido) {
        throw new Error("Datos de acceso incompletos");
      }
      
      // Llamar a la API de descarga
      const url = `/agenda/contenidoAccederDescargar?id_venta=${id_venta}&id_contenido=${id_contenido}&id_archivo=${idArchivo}&codigo=${codigo_usado}`;
      const response = await invokeApi(url, "get");
      
      if (response.error) {
        throw new Error(response.error.message || "Error al generar enlace de descarga");
      }
      
      if (response.statusCode === 200 && response.downloadUrl) {
        // Iniciar descarga usando la URL temporal
        window.open(response.downloadUrl, '_blank');
      } else {
        throw new Error("Respuesta inesperada del servidor");
      }
      
    } catch (error) {
      console.error("Error al descargar archivo:", error);
      limpiarAccesoYRedirigir();
    }
  };

  const renderDescripcion = () => {
    if (!contenidoCompleto?.contenido?.contenido_descripcion) return null;
    
    const descripcion = contenidoCompleto.contenido.contenido_descripcion;
    const paragraphs = descripcion
      .split("\n")
      .filter((paragraph) => paragraph.trim() !== "");

    return (
      <>
        {paragraphs.map((paragraph, index) => (
          <Typography
            key={index}
            sx={{
              mb: 2,
              fontFamily: "Quicksand-Regular",
              whiteSpace: "pre-wrap",
              color: "#191C1F",
              fontSize: { md: "16px", xs: "14px" },
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </>
    );
  };

  // Componente del check animado (Demo 1)
  const CheckIcon = () => (
    <div 
      style={{
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        backgroundColor: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto",
        animation: showCheck ? 'popIn 0.3s ease-out' : 'none'
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path 
          d="M20 6L9 17L4 12" 
          stroke="#10B981" 
          strokeWidth="3" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{
            pathLength: 1,
            strokeDasharray: showCheck ? '1' : '0',
            strokeDashoffset: showCheck ? '0' : '1',
            animation: showCheck ? 'drawCheck 0.5s ease-out 0.2s both' : 'none'
          }}
        />
      </svg>
    </div>
  );

  // Si hay error inicial, mostrar el estado de error
  if (hasInitialError) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <ErrorState />
      </>
    );
  }

  if (currentPhase === 1) {
    // FASE 1: Solicitar código
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        
        <style jsx>{`
          @keyframes drawCheck {
            0% { stroke-dashoffset: 1; }
            100% { stroke-dashoffset: 0; }
          }
          
          @keyframes popIn {
            0% { transform: scale(0); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          
          @keyframes colorPulse {
            0%, 100% { 
              color: #9CA3AF;
              opacity: 1;
            }
            50% { 
              color: #D1D5DB;
              opacity: 0.7;
            }
          }
        `}</style>
        
        <img
          src={logoImage}
          alt="Clinifica Logo"
          style={{
            width: isMobile ? "150px" : "200px",
            height: "auto",
            paddingTop: "15px",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            padding: 15,
          }}
        />
        
        <Box
          sx={{
            maxWidth: isMobile ? "94%" : "800px",
            width: isMobile ? "94%" : "90%",
            mx: "auto",
            bgcolor: "#fbfafe",
            boxShadow: 1,
            marginTop: 2,
            marginBottom: 8,
            borderRadius: 2,
            p: isMobile ? 2 : 4,
          }}
        >
          {/* Título del contenido */}
          <Typography 
            sx={{ 
              fontFamily: "Quicksand-Bold", 
              fontSize: { md: "28px", xs: "24px" },
              color: "#191C1F",
              mb: 0.5
            }}
          >
            {isLoading || loadingFromCookies ? (
              <Skeleton animation="wave" variant="text" height={40} />
            ) : (
              datosIniciales?.contenido?.nombre
            )}
          </Typography>

          {/* Imagen principal del contenido */}
          <Box sx={{ mb: 3 }}>
            {isLoading || loadingFromCookies ? (
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={isMobile ? 250 : 400}
                sx={{ borderRadius: 2 }}
              />
            ) : (
              datosIniciales?.contenido?.imagen && 
              renderImageWithBlur(
                datosIniciales.contenido.imagen,
                datosIniciales.contenido.nombre,
                "100%",
                isMobile ? "250px" : "400px",
                "main-content-image",
                contentImageLoadingStates,
                setContentImageLoadingStates
              )
            )}
          </Box>

          {/* Información del profesional */}
          <ProfessionalInfo 
            profesionalInfo={datosIniciales?.profesional} 
            contenidoNombre={datosIniciales?.contenido?.nombre}
            isLoading={isLoading || loadingFromCookies}
          />

          {/* Caja de solicitud de código */}
          {!isLoading && !loadingFromCookies && (
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 3,
                backgroundColor: "#ffffff",
                mb: 2,
                opacity: contentTransitioning ? 0 : 1,
                transform: contentTransitioning ? 'translateY(20px)' : 'translateY(0)',
                transition: 'all 0.8s ease-in-out'
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { md: "16px", xs: "14px" },
                  color: "#191C1F",
                  mb: 2,
                  textAlign: "center"
                }}
              >
                Hola{" "}
                <Box 
                  component="span" 
                  sx={{ fontFamily: "Quicksand-Bold" }}
                >
                  {datosIniciales?.comprador?.nombre}
                </Box>
                , por favor ingresa el código que te acabamos de enviar al correo{" "}
                <Box 
                  component="span" 
                  sx={{ fontFamily: "Quicksand-Bold" }}
                >
                  {datosIniciales?.comprador?.correo}
                </Box>
              </Typography>

              <Typography
                sx={{
                  pt: 1,
                  fontFamily: "Quicksand-Regular",
                  fontSize: { md: "16px", xs: "14px" },
                  color: "#191C1F",
                  mb: 1
                }}
              >
                Código de acceso:
              </Typography>
              
              <Box sx={getFieldContainerStyle()}>
                <TextField
                  value={autoSimulation ? simulatedCode : codigo}
                  onChange={handleCodigoChange}
                  placeholder="Ingrese su código"
                  variant="standard"
                  fullWidth
                  disabled={isValidating || showCheck || autoSimulation}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontFamily: "Quicksand-Regular",
                      color: "#4F4F4F",
                      fontSize: { md: "16px", xs: "14px" },
                      "& input": {
                        padding: 0,
                        textAlign: "center"
                      },
                    },
                  }}
                />
              </Box>

              {(error || warningMessage || (!codigo.trim() && codigo !== "")) && !autoSimulation && (
                <Box
                    sx={{
                    backgroundColor: "#FFF9E6",
                    padding: 1,
                    borderRadius: 2,
                    mt: 1,
                    textAlign: "center",
                    }}
                >
                    <Typography
                    sx={{
                        fontFamily: "Quicksand-Regular",
                        fontSize: { md: "14px", xs: "12px" },
                        color: "#836516",
                    }}
                    >
                    {warningMessage || error || "Debes ingresar el código"}
                    </Typography>
                </Box>
                )}

              <Button
                sx={{
                    fontSize: { xs: "14px", md: "16px" },
                    fontFamily: "Quicksand-Bold",
                    backgroundColor: showCheck ? "#10B981 !important" : (codigo.trim() || simulatedCode ? "#3B82F6" : "#9CA3AF"),
                    "&:hover": { 
                    backgroundColor: showCheck ? "#10B981 !important" : (codigo.trim() || simulatedCode ? "#2563EB" : "#9CA3AF") 
                    },
                    "&:disabled": {
                      backgroundColor: showCheck ? "#10B981 !important" : undefined
                    },
                    borderRadius: 2,
                    width: "100%",
                    paddingTop: 2,
                    paddingBottom: 2,
                    mt: 2,
                    opacity: buttonFading ? 0 : 1,
                    transform: buttonFading ? 'translateY(-10px)' : 'translateY(0)',
                    transition: 'all 0.5s ease-in-out'
                }}
                variant="contained"
                disabled={isValidating || (!codigo.trim() && !simulatedCode) || showCheck}
                onClick={handleValidarCodigo}
                >
                {showCheck ? (
                    <CheckIcon />
                ) : isValidating ? (
                    <CircularProgress 
                    size={20} 
                    sx={{ 
                        color: "white"
                    }} 
                    />
                ) : (
                    "Validar código"
                )}
                </Button>

              {/* Texto de reenvío */}
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { md: "14px", xs: "12px" },
                  color: "#000000",
                  mt: 2,
                  textAlign: "center"
                }}
              >
                ¿no recibiste el código?{' '}
                <Box 
                  component="span"
                  sx={{
                    fontWeight: 'bold',
                    color: estadoReenvio === 'reenviando' ? '#9CA3AF' : (estadoReenvio === 'reenviado' ? '#3B82F6' : '#000000'),
                    cursor: estadoReenvio === 'normal' ? 'pointer' : 'default',
                    transition: 'color 0.3s ease-in-out',
                    animation: estadoReenvio === 'reenviando' ? 'colorPulse 1.2s ease-in-out infinite' : 'none',
                    '&:hover': estadoReenvio === 'normal' ? {
                      textDecoration: 'underline'
                    } : {}
                  }}
                  onClick={estadoReenvio === 'normal' ? handleReenviarCodigo : undefined}
                >
                  {estadoReenvio === 'reenviando' ? 'reenviando' : (estadoReenvio === 'reenviado' ? 'reenviado' : 'reenviar')}
                </Box>
              </Typography>
            </Box>
          )}

          {/* Skeleton de la caja cuando hay cookies */}
          {loadingFromCookies && (
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 3,
                backgroundColor: "#ffffff",
                mb: 2
              }}
            >
              <Skeleton animation="wave" variant="text" height={60} sx={{ mb: 2 }} />
              <Skeleton animation="wave" variant="text" height={30} sx={{ mb: 2 }} />
              <Skeleton animation="wave" variant="rectangular" height={50} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton animation="wave" variant="rectangular" height={50} sx={{ borderRadius: 1 }} />
            </Box>
          )}
        </Box>
      </>
    );
  }

  // FASE 2: Mostrar contenido
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>
      
      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />
      
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        {/* Título del contenido */}
        <Typography 
          sx={{ 
            fontFamily: "Quicksand-Bold", 
            fontSize: { md: "28px", xs: "24px" },
            color: "#191C1F",
            mb: 0.5
          }}
        >
          {contenidoCompleto?.contenido?.contenido_nombre}
        </Typography>

        {/* Imagen principal del contenido */}
        <Box sx={{ mb: 3 }}>
          {contenidoCompleto?.contenido?.contenido_imagen && 
            renderImageWithBlur(
              contenidoCompleto.contenido.contenido_imagen,
              contenidoCompleto.contenido.contenido_nombre,
              "100%",
              isMobile ? "250px" : "400px",
              "main-content-image",
              contentImageLoadingStates,
              setContentImageLoadingStates
            )
          }
        </Box>

        {/* Información del profesional */}
        <ProfessionalInfo 
          profesionalInfo={contenidoCompleto?.profesional} 
          contenidoNombre={contenidoCompleto?.contenido?.contenido_nombre}
          isLoading={false}
        />

        {/* Contenedor principal del contenido desbloqueado */}
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 3,
            backgroundColor: "#ffffff",
            mb: 2,
            opacity: contentTransitioning ? 0 : 1,
            transform: contentTransitioning ? 'translateY(20px)' : 'translateY(0)',
            transition: 'all 0.8s ease-in-out'
          }}
        >
          {/* Video de YouTube (solo si existe URL válida) */}
          {contenidoCompleto?.contenido?.contenido_video_url && (
            <YouTubeEmbed videoUrl={contenidoCompleto.contenido.contenido_video_url} />
          )}

          {/* Sección Descripción */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "20px", xs: "18px" },
                color: "#191C1F",
                mb: 2
              }}
            >
              Descripción del contenido
            </Typography>
            {renderDescripcion()}
          </Box>

          {/* Sección Mensaje */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "20px", xs: "18px" },
                color: "#191C1F",
                mb: 2
              }}
            >
              Mensaje del profesional e instrucciones de uso:
            </Typography>
            
            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                minHeight: { xs: "60px", md: "80px" },
                backgroundColor: "white",
                mb: 1,
                padding: "12px",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  whiteSpace: "pre-wrap",
                  color: "#191C1F",
                  fontSize: { md: "16px", xs: "14px" },
                }}
              >
                {contenidoCompleto?.contenido?.contenido_mensaje}
              </Typography>
            </Box>
          </Box>

          {/* Sección Archivos */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "20px", xs: "18px" },
                color: "#191C1F",
                mb: 2
              }}
            >
              Archivos disponibles para descarga
            </Typography>
            
            {sesionDescargaExpirada && (
              <Box
                sx={{
                  backgroundColor: "#FFF9E6",
                  padding: 1,
                  borderRadius: 2,
                  mb: 1,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "14px", xs: "12px" },
                    color: "#836516",
                  }}
                >
                  Tu sesión de descarga terminó. Puedes seguir viendo el video, para descargar archivos{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 'bold',
                      color: '#000000',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => window.location.reload()}
                  >
                    recarga la página
                  </Box>{' '}
                  y te enviaremos un nuevo código.
                </Typography>
              </Box>
            )}
            
            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                minHeight: { xs: "100px", md: "120px" },
                maxHeight: { xs: "300px", md: "400px" },
                backgroundColor: "white",
                mb: 1,
                padding: "6px 12px 12px 12px",
                display: "flex",
                flexDirection: "column"
              }}
            >
              {contenidoCompleto?.archivos && contenidoCompleto.archivos.length > 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    overflow: "auto",
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '3px',
                      '&:hover': {
                        background: '#555',
                      }
                    }
                  }}
                >
                  {contenidoCompleto.archivos.map((archivo) => (
                    <Box
                      key={archivo.id_archivo}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 0.5,
                        px: 0,
                        gap: 1
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Quicksand-Regular",
                          fontSize: { xs: "12px", md: "14px" },
                          color: "#09090a",
                          flex: 1
                        }}
                      >
                        {truncarNombre(archivo.archivo_nombre)} ({Math.ceil(archivo.archivo_tamaño / (1024 * 1024))}MB)
                      </Typography>
                      
                      <Button
                        onClick={() => handleDescargarArchivo(archivo.id_archivo)}
                        disabled={sesionDescargaExpirada}
                        sx={{
                          fontFamily: "Quicksand-Bold",
                          fontSize: { xs: "12px", md: "14px" },
                          textTransform: "none",
                          color: "#1976D2",
                          minWidth: "auto",
                          p: 0.5,
                          "&:hover": {
                            backgroundColor: "#f0f8ff"
                          }
                        }}
                      >
                        descargar
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    minHeight: { xs: "100px", md: "120px" }
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { xs: "14px", md: "16px" },
                      color: "#9CA3AF",
                      textAlign: "center"
                    }}
                  >
                    Sin contenidos disponibles
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default ContenidoAcceder;
